import postcss from "postcss";
import postcssScss from "postcss-scss";
import valueParser from "postcss-value-parser";
import ts from "typescript";
import { all as knownCssProperties } from "known-css-properties";

function kebabToCamel(str: string): string {
  return str.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

function camelToKebab(str: string): string {
  return str.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}

const CSS_STYLE_PROPERTIES = new Set<string>(
  knownCssProperties.flatMap((prop) => [
    prop, // kebab-case: "background-color"
    kebabToCamel(prop), // camelCase: "backgroundColor"
  ]),
);

function isCssPropertyName(name: string): boolean {
  return CSS_STYLE_PROPERTIES.has(name) || name.startsWith("--");
}

const PX_NUMERIC_PROPERTIES = new Set<string>([
  "padding",
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
  "margin",
  "margin-top",
  "margin-right",
  "margin-bottom",
  "margin-left",
  "gap",
  "row-gap",
  "column-gap",
  "width",
  "height",
  "min-width",
  "min-height",
  "max-width",
  "max-height",
  "top",
  "right",
  "bottom",
  "left",
  "border-width",
  "border-top-width",
  "border-right-width",
  "border-bottom-width",
  "border-left-width",
  "border-radius",
  "border-top-left-radius",
  "border-top-right-radius",
  "border-bottom-left-radius",
  "border-bottom-right-radius",
  "outline-width",
  "font-size",
  "letter-spacing",
]);

function isPxNumericProperty(name: string): boolean {
  if (name.startsWith("--")) return false;
  const normalized = name.includes("-")
    ? name.toLowerCase()
    : camelToKebab(name);
  return PX_NUMERIC_PROPERTIES.has(normalized);
}

function isStyleVariableName(name: string): boolean {
  return /styles?$/i.test(name);
}

function isStyleJsxAttribute(node: ts.JsxAttribute): boolean {
  return ts.isIdentifier(node.name) && node.name.text === "style";
}

function getPropertyName(name: ts.PropertyName): string | undefined {
  if (ts.isIdentifier(name)) return name.text;
  if (ts.isStringLiteral(name)) return name.text;
  return undefined;
}

/**
 * Represents a matched value in source code with position information.
 */
export interface Match {
  value: string;
  line: number;
  column: number;
  endLine: number;
  endColumn: number;
}

/**
 * Result from parsing CSS/TSX code.
 */
export interface ParseResult {
  colors: Match[];
  dimensions: Match[];
}

const COLOR_FUNCTIONS = new Set([
  "rgb",
  "rgba",
  "hsl",
  "hsla",
  "hwb",
  "lab",
  "lch",
  "oklch",
  "oklab",
  "color",
]);

const HEX_COLOR_REGEX =
  /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

const CSS_UNITS = "px|rem|em|%|vw|vh|vmin|vmax|ch|ex|pt|pc|in|cm|mm";

const CSS_NAMED_COLORS = new Set([
  "black",
  "silver",
  "gray",
  "white",
  "maroon",
  "red",
  "purple",
  "fuchsia",
  "green",
  "lime",
  "olive",
  "yellow",
  "navy",
  "blue",
  "teal",
  "aqua",
  "orange",
  "aliceblue",
  "antiquewhite",
  "aquamarine",
  "azure",
  "beige",
  "bisque",
  "blanchedalmond",
  "blueviolet",
  "brown",
  "burlywood",
  "cadetblue",
  "chartreuse",
  "chocolate",
  "coral",
  "cornflowerblue",
  "cornsilk",
  "crimson",
  "cyan",
  "darkblue",
  "darkcyan",
  "darkgoldenrod",
  "darkgray",
  "darkgreen",
  "darkgrey",
  "darkkhaki",
  "darkmagenta",
  "darkolivegreen",
  "darkorange",
  "darkorchid",
  "darkred",
  "darksalmon",
  "darkseagreen",
  "darkslateblue",
  "darkslategray",
  "darkslategrey",
  "darkturquoise",
  "darkviolet",
  "deeppink",
  "deepskyblue",
  "dimgray",
  "dimgrey",
  "dodgerblue",
  "firebrick",
  "floralwhite",
  "forestgreen",
  "gainsboro",
  "ghostwhite",
  "gold",
  "goldenrod",
  "greenyellow",
  "grey",
  "honeydew",
  "hotpink",
  "indianred",
  "indigo",
  "ivory",
  "khaki",
  "lavender",
  "lavenderblush",
  "lawngreen",
  "lemonchiffon",
  "lightblue",
  "lightcoral",
  "lightcyan",
  "lightgoldenrodyellow",
  "lightgray",
  "lightgreen",
  "lightgrey",
  "lightpink",
  "lightsalmon",
  "lightseagreen",
  "lightskyblue",
  "lightslategray",
  "lightslategrey",
  "lightsteelblue",
  "lightyellow",
  "limegreen",
  "linen",
  "magenta",
  "mediumaquamarine",
  "mediumblue",
  "mediumorchid",
  "mediumpurple",
  "mediumseagreen",
  "mediumslateblue",
  "mediumspringgreen",
  "mediumturquoise",
  "mediumvioletred",
  "midnightblue",
  "mintcream",
  "mistyrose",
  "moccasin",
  "navajowhite",
  "oldlace",
  "olivedrab",
  "orangered",
  "orchid",
  "palegoldenrod",
  "palegreen",
  "paleturquoise",
  "palevioletred",
  "papayawhip",
  "peachpuff",
  "peru",
  "pink",
  "plum",
  "powderblue",
  "rosybrown",
  "royalblue",
  "saddlebrown",
  "salmon",
  "sandybrown",
  "seagreen",
  "seashell",
  "sienna",
  "skyblue",
  "slateblue",
  "slategray",
  "slategrey",
  "snow",
  "springgreen",
  "steelblue",
  "tan",
  "thistle",
  "tomato",
  "turquoise",
  "violet",
  "wheat",
  "whitesmoke",
  "yellowgreen",
  "rebeccapurple",
  "transparent",
]);

interface ParseCssOptions {
  syntax?: "css" | "scss";
}

/**
 * Parse a CSS string and extract colors and dimensions with positions.
 */
export function parseCss(
  code: string,
  options: ParseCssOptions = {},
): ParseResult {
  const colors: Match[] = [];
  const dimensions: Match[] = [];

  try {
    const root =
      options.syntax === "scss"
        ? postcssScss.parse(code)
        : postcss.parse(code);

    root.walkDecls((decl) => {
      const declSource = decl.source;
      if (!declSource?.start) return;

      const propLength = decl.prop.length;
      const betweenLength = decl.raws.between?.length ?? 1; // typically ": "
      const valueStartOffset = propLength + betweenLength;

      const parsed = valueParser(decl.value);

      parsed.walk((node) => {
        if (
          node.type === "function" &&
          COLOR_FUNCTIONS.has(node.value.toLowerCase())
        ) {
          const fullValue = valueParser.stringify(node);
          const match = createMatchFromNode(
            fullValue,
            node.sourceIndex,
            declSource.start!.line,
            declSource.start!.column + valueStartOffset,
          );
          colors.push(match);
          return false; // Skip child nodes
        }

        if (node.type === "word" && HEX_COLOR_REGEX.test(node.value)) {
          const match = createMatchFromNode(
            node.value,
            node.sourceIndex,
            declSource.start!.line,
            declSource.start!.column + valueStartOffset,
          );
          colors.push(match);
        }

        if (
          node.type === "word" &&
          CSS_NAMED_COLORS.has(node.value.toLowerCase())
        ) {
          const match = createMatchFromNode(
            node.value,
            node.sourceIndex,
            declSource.start!.line,
            declSource.start!.column + valueStartOffset,
          );
          colors.push(match);
        }

        if (node.type === "word") {
          const dimMatch = isDimension(node.value);
          if (dimMatch) {
            const match = createMatchFromNode(
              node.value,
              node.sourceIndex,
              declSource.start!.line,
              declSource.start!.column + valueStartOffset,
            );
            dimensions.push(match);
          }
        }
      });
    });
  } catch {
    return { colors: [], dimensions: [] };
  }

  return { colors, dimensions };
}

/**
 * Parse an SCSS string and extract colors and dimensions with positions.
 */
export function parseScss(code: string): ParseResult {
  return parseCss(code, { syntax: "scss" });
}

function createMatchFromNode(
  value: string,
  sourceIndex: number,
  baseLine: number,
  baseColumn: number,
): Match {
  const column = baseColumn + sourceIndex;

  return {
    value,
    line: baseLine,
    column,
    endLine: baseLine,
    endColumn: column + value.length,
  };
}

const DIMENSION_VALUE_REGEX = new RegExp(
  `^(-?\\d+(?:\\.\\d+)?)(${CSS_UNITS})$`,
  "i",
);

function isDimension(value: string): boolean {
  return DIMENSION_VALUE_REGEX.test(value);
}

/**
 * Parse a TSX/TS/JS/JSX string and extract colors and dimensions using TypeScript AST.
 * Extracts values from JSX style props and style-named object literals.
 */
function parseTsLike(code: string, scriptKind: ts.ScriptKind): ParseResult {
  const colors: Match[] = [];
  const dimensions: Match[] = [];

  let sourceFile: ts.SourceFile;
  try {
    sourceFile = ts.createSourceFile(
      "file.tsx",
      code,
      ts.ScriptTarget.Latest,
      true,
      scriptKind,
    );
  } catch {
    return { colors: [], dimensions: [] };
  }

  function visit(node: ts.Node) {
    if (ts.isJsxAttribute(node) && isStyleJsxAttribute(node)) {
      const initializer = node.initializer;
      if (initializer && ts.isJsxExpression(initializer)) {
        const expression = initializer.expression;
        if (expression) {
          extractFromExpression(expression, sourceFile, colors, dimensions);
        }
      }
    }

    if (
      ts.isVariableDeclaration(node) &&
      node.initializer &&
      ts.isIdentifier(node.name) &&
      isStyleVariableName(node.name.text)
    ) {
      extractFromExpression(node.initializer, sourceFile, colors, dimensions);
    }

    ts.forEachChild(node, visit);
  }

  visit(sourceFile);
  return { colors, dimensions };
}

export function parseTsx(code: string): ParseResult {
  return parseTsLike(code, ts.ScriptKind.TSX);
}

export function parseTs(code: string): ParseResult {
  return parseTsLike(code, ts.ScriptKind.TS);
}

export function parseJs(code: string): ParseResult {
  return parseTsLike(code, ts.ScriptKind.JS);
}

export function parseJsx(code: string): ParseResult {
  return parseTsLike(code, ts.ScriptKind.JSX);
}

function extractFromStyleObjectLiteral(
  node: ts.ObjectLiteralExpression,
  sourceFile: ts.SourceFile,
  colors: Match[],
  dimensions: Match[],
): void {
  for (const prop of node.properties) {
    if (!ts.isPropertyAssignment(prop)) continue;

    const propName = getPropertyName(prop.name);
    const initializer = prop.initializer;

    if (ts.isObjectLiteralExpression(initializer)) {
      extractFromStyleObjectLiteral(initializer, sourceFile, colors, dimensions);
      continue;
    }

    if (propName && isCssPropertyName(propName)) {
      extractFromExpression(
        initializer,
        sourceFile,
        colors,
        dimensions,
        propName,
      );
    }
  }
}

/**
 * Extract colors and dimensions from an expression node.
 * Handles string/template literals, numeric literals, object literals, and ternaries.
 */
function extractFromExpression(
  node: ts.Node,
  sourceFile: ts.SourceFile,
  colors: Match[],
  dimensions: Match[],
  propertyName?: string,
): void {
  if (ts.isStringLiteral(node)) {
    const value = node.text;
    const startPos = node.getStart(sourceFile);
    const { line, character } = sourceFile.getLineAndCharacterOfPosition(startPos);
    // +1 for the opening quote, +1 for 1-based line/column
    extractValuesFromString(value, line + 1, character + 2, colors, dimensions);
  }

  if (ts.isNoSubstitutionTemplateLiteral(node)) {
    const { line, column } = getTemplateLiteralBasePosition(node, sourceFile);
    extractValuesFromString(node.text, line, column, colors, dimensions);
  }

  if (ts.isTemplateExpression(node)) {
    if (node.head.text) {
      const { line, column } = getTemplateLiteralBasePosition(
        node.head,
        sourceFile,
      );
      extractValuesFromString(node.head.text, line, column, colors, dimensions);
    }

    for (const span of node.templateSpans) {
      if (!span.literal.text) continue;
      const { line, column } = getTemplateLiteralBasePosition(
        span.literal,
        sourceFile,
      );
      extractValuesFromString(
        span.literal.text,
        line,
        column,
        colors,
        dimensions,
      );
    }
  }

  if (ts.isNumericLiteral(node) && propertyName && isPxNumericProperty(propertyName)) {
    const startPos = node.getStart(sourceFile);
    const { line, character } = sourceFile.getLineAndCharacterOfPosition(startPos);
    const column = character + 1;
    const value = `${node.text}px`;
    dimensions.push({
      value,
      line: line + 1,
      column,
      endLine: line + 1,
      endColumn: column + node.text.length,
    });
  }

  if (ts.isObjectLiteralExpression(node)) {
    extractFromStyleObjectLiteral(node, sourceFile, colors, dimensions);
  }

  if (ts.isConditionalExpression(node)) {
    extractFromExpression(
      node.whenTrue,
      sourceFile,
      colors,
      dimensions,
      propertyName,
    );
    extractFromExpression(
      node.whenFalse,
      sourceFile,
      colors,
      dimensions,
      propertyName,
    );
  }
}

function getTemplateLiteralBasePosition(
  node: ts.TemplateLiteralLikeNode,
  sourceFile: ts.SourceFile,
): { line: number; column: number } {
  const startPos = node.getStart(sourceFile);
  const nodeText = node.getText(sourceFile);
  const rawText = node.text ?? "";
  const offset = rawText ? nodeText.indexOf(rawText) : 0;
  const pos = startPos + (offset >= 0 ? offset : 0);
  const { line, character } = sourceFile.getLineAndCharacterOfPosition(pos);
  return { line: line + 1, column: character + 1 };
}

/**
 * Extract color and dimension values from a string value.
 */
function extractValuesFromString(
  value: string,
  baseLine: number,
  baseColumn: number,
  colors: Match[],
  dimensions: Match[],
): void {
  const parsed = valueParser(value);

  parsed.walk((node) => {
    if (node.type === "function" && COLOR_FUNCTIONS.has(node.value.toLowerCase())) {
      const fullValue = valueParser.stringify(node);
      colors.push(
        createMatchFromNode(fullValue, node.sourceIndex, baseLine, baseColumn),
      );
      return false;
    }

    if (node.type !== "word") return;

    if (HEX_COLOR_REGEX.test(node.value)) {
      colors.push(
        createMatchFromNode(node.value, node.sourceIndex, baseLine, baseColumn),
      );
      return;
    }

    if (CSS_NAMED_COLORS.has(node.value.toLowerCase())) {
      colors.push(
        createMatchFromNode(node.value, node.sourceIndex, baseLine, baseColumn),
      );
      return;
    }

    if (isDimension(node.value)) {
      dimensions.push(
        createMatchFromNode(node.value, node.sourceIndex, baseLine, baseColumn),
      );
    }
  });
}
